import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UnitsPicker } from "@/components/UnitsPicker";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NONE = "none";
const clean = (v) => (v === NONE || v === undefined || v === null ? "" : v);
const OTHER = "__other__";

const EMPTY = {
  card_id: null,
  user_id: null,
  name: "",
  email: "",
  plant: "",
  band: "",
  working_title: "",
  lead_units: [],
  department: "",
  designation: "",
  designation_grade: NONE,
  sail_id: "",
  organisation: "",
  super_admin: false,
  contact_number: "",
  whatsapp_number: "",
  landline: "",
  stationed_at: "",
  org_category: NONE,
  org_unit: NONE,
  role: NONE,
  units: [],
  access_status: NONE,
  invite: false,
};

const Field = ({ label, hint, wide, children }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className="label-caps">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

const Block = ({ title, note, children, testId }) => (
  <div className="border border-border p-4" data-testid={testId}>
    <div className="label-caps text-primary">{title}</div>
    {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    <div className="mt-3 grid gap-4 sm:grid-cols-2">{children}</div>
  </div>
);

/** The one form for a person — their card in the team directory and what they can do, together. */
export const PersonFormDialog = ({ open, onOpenChange, person, meta, options, plants, onSaved,
                                   selfOnly = false }) => {
  const { user } = useAuth();
  const isSystemAdmin = user?.role === "system_admin";
  const isSpoc = user?.role === "spoc";
  const editing = !!(person?.card_id || person?.user_id);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState("");
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setWarn("");
    setRemoving(false);
    setReason("");
    setForm({
      ...EMPTY,
      ...Object.fromEntries(
        Object.entries(person || {}).map(([k, v]) => [
          k,
          ["role", "access_status", "designation_grade", "org_category", "org_unit"].includes(k)
            ? v || NONE
            : v ?? EMPTY[k] ?? "",
        ]),
      ),
      super_admin: (meta?.super_admins || [])
        .map((e) => e.toLowerCase())
        .includes(((person || {}).email || "").trim().toLowerCase()),
    });
  }, [open, person, meta]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mayAccess =
    !selfOnly &&
    (isSystemAdmin ||
      (isSpoc &&
        user?.plant === form.plant &&
        ["spoc_member", "viewer", NONE, "", null].includes(person?.role || NONE)));

  // A partner's officer is not a SAIL employee: no personnel number is asked of them, only who
  // they work for. Their role says so, and so does an identity that is not @sail.in.
  const domain = meta?.allowed_email_domain || "sail.in";
  const partner =
    (meta?.partner_roles || []).includes(clean(form.role)) ||
    (!!form.email.trim() && !form.email.trim().toLowerCase().endsWith(`@${domain}`));
  const superAdmins = (meta?.super_admins || []).map((e) => e.toLowerCase());
  const amSuperAdmin = !!superAdmins.length;
  const mayManageSuper = !!meta?.may_manage_super_admins;
  const isFounderEmail = (meta?.super_admin_founders || [])
    .map((e) => e.toLowerCase())
    .includes(form.email.trim().toLowerCase());
  const wasSuperAdmin = superAdmins.includes(form.email.trim().toLowerCase());

  const save = async (acknowledge = false, remove = false) => {
    if (!form.name.trim() || !form.plant) {
      toast.error("The officer's name and their unit are needed.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/people/person", {
        card_id: form.card_id || null,
        user_id: form.user_id || null,
        name: form.name.trim(),
        email: form.email.trim() || null,
        plant: form.plant,
        band: form.band || null,
        working_title: form.working_title || null,
        lead_units: form.lead_units || [],
        department: form.department || null,
        designation: form.designation || null,
        designation_grade: clean(form.designation_grade) || null,
        sail_id: form.sail_id || null,
        organisation: form.organisation || null,
        super_admin:
          mayManageSuper && !selfOnly && form.super_admin !== wasSuperAdmin
            ? !!form.super_admin
            : null,
        contact_number: form.contact_number || null,
        whatsapp_number: form.whatsapp_number || null,
        landline: form.landline || null,
        stationed_at: form.stationed_at || null,
        org_category: clean(form.org_category) || null,
        org_unit: clean(form.org_unit) || null,
        role: mayAccess ? clean(form.role) || null : null,
        units: mayAccess ? form.units || [] : null,
        access_status: mayAccess && isSystemAdmin ? clean(form.access_status) || null : null,
        invite: !!form.invite,
        remove,
        reason: reason.trim() || null,
        acknowledge_orphans: acknowledge,
      });
      if (remove) {
        toast.success(
          data.awaiting_verification
            ? `Sent to the ${form.plant} SPOC — ${form.name} stays on the list until they approve.`
            : `${form.name} taken off the ${form.plant} team, and their access switched off.`,
        );
      } else {
        const bits = [];
        if (data.access_changes?.length) bits.push(`${data.access_changes.length} access change(s)`);
        if (data.invited) bits.push("invitation sent");
        if (data.email_sent) bits.push("the officer has been told by mail");
        toast.success(
          data.awaiting_verification
            ? `Recorded and sent to the ${form.plant} SPOC for approval.`
            : `${form.name} saved${bits.length ? ` — ${bits.join(", ")}` : ""}.`,
        );
        if (data.super_admin_note) toast.success(data.super_admin_note);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      if (err?.response?.status === 409) setWarn(apiError(err));
      else toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const postings = (options?.categories || [])
    .filter((c) => c.category === clean(form.org_category))
    .flatMap((c) => c.units);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto rounded-none bg-card sm:max-w-3xl"
        data-testid="person-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">
            {editing ? form.name || "This person" : "Add a person"}
          </DialogTitle>
          <DialogDescription>
            Everything about a person in one form — who they are, where they sit, their place in the
            team and what they can do on the portal. One Save, one line on the trail.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save(false, false);
          }}
        >
          <Block title="Who they are" testId="person-identity">
            <Field label="Name *" wide>
              <Input
                className="mt-1 h-11 rounded-none"
                data-testid="person-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="E-mail" hint="The e-mail links their card and their portal account.">
              <Input
                className="font-data mt-1 h-11 rounded-none"
                data-testid="person-email"
                placeholder="officer@sail.in"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field
              label={partner ? "Organisation" : "SAIL personnel number"}
              hint={partner
                ? "A partner's officer carries no SAIL personnel number."
                : ""}
            >
              <Input
                className="font-data mt-1 h-11 rounded-none"
                data-testid={partner ? "person-organisation" : "person-sail-id"}
                placeholder={partner ? "McKinsey & Company" : ""}
                value={partner ? form.organisation : form.sail_id}
                onChange={(e) =>
                  set(partner ? "organisation" : "sail_id", e.target.value)
                }
              />
            </Field>
            <Field label="Mobile">
              <Input
                className="font-data mt-1 h-11 rounded-none"
                data-testid="person-mobile"
                value={form.contact_number}
                onChange={(e) => set("contact_number", e.target.value)}
              />
            </Field>
            <Field label="WhatsApp">
              <Input
                className="font-data mt-1 h-11 rounded-none"
                data-testid="person-whatsapp"
                value={form.whatsapp_number}
                onChange={(e) => set("whatsapp_number", e.target.value)}
              />
            </Field>
            <Field label="Landline">
              <Input
                className="font-data mt-1 h-11 rounded-none"
                data-testid="person-landline"
                value={form.landline}
                onChange={(e) => set("landline", e.target.value)}
              />
            </Field>
            <Field label="Stationed at">
              <Input
                className="mt-1 h-11 rounded-none"
                data-testid="person-stationed-at"
                placeholder="e.g. Bokaro"
                value={form.stationed_at}
                onChange={(e) => set("stationed_at", e.target.value)}
              />
            </Field>
          </Block>

          <Block
            title="Where they sit"
            note={
              selfOnly
                ? "Where you are posted is SDTD's to set — ask them if it needs correcting."
                : "The unit decides whose data they work on, and where their card appears."
            }
            testId="person-placement"
          >
            <Field label="Unit *">
              <Select
                value={form.plant}
                onValueChange={(v) => set("plant", v)}
                disabled={selfOnly}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-plant">
                  <SelectValue placeholder="Choose the unit" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  {(plants || []).map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.code} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Department">
              <Input
                className="mt-1 h-11 rounded-none"
                data-testid="person-department"
                placeholder="e.g. SMS-II"
                value={form.department}
                onChange={(e) => set("department", e.target.value)}
              />
            </Field>
            <Field label="Designation and grade">
              <Select
                value={form.designation_grade}
                onValueChange={(v) => set("designation_grade", v)}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-grade">
                  <SelectValue placeholder="Choose the grade" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  <SelectItem value={NONE}>Not given</SelectItem>
                  {(options?.designations || []).map((d) => (
                    <SelectItem key={d.grade} value={d.grade}>
                      {d.grade} — {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select
                value={form.org_category}
                onValueChange={(v) => setForm((f) => ({ ...f, org_category: v, org_unit: NONE }))}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-category">
                  <SelectValue placeholder="Choose the category" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  <SelectItem value={NONE}>Not given</SelectItem>
                  {(options?.categories || []).map((c) => (
                    <SelectItem key={c.category} value={c.category}>
                      {c.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Posted at">
              <Select
                value={form.org_unit}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    org_unit: v,
                    plant: options?.unit_plants?.[v] || f.plant,
                  }))
                }
                disabled={!postings.length}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-posting">
                  <SelectValue placeholder="Choose the plant / unit" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  <SelectItem value={NONE}>Not given</SelectItem>
                  {postings.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Block>

          <Block
            title="Their place in the team"
            note={
              selfOnly
                ? "Which team you belong to is set by SDTD or your unit's SPOC."
                : "The team decides where their card appears on the People page — the SPOC and the delivery lead on top, then the CoDE team, then the departmental officers."
            }
            testId="person-team"
          >
            <Field label="Team">
              <Select
                value={form.band}
                onValueChange={(v) => set("band", v)}
                disabled={selfOnly}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-band">
                  <SelectValue placeholder="Choose the team" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  {(meta?.team_bands || []).map((b) => (
                    <SelectItem key={b} value={b}>
                      {b} — {meta?.team_band_help?.[b]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Role in the team" hint="SPOC, CoDE Member, Desk Officer and the rest — a designation, not an access right.">
              <Select
                value={
                  (meta?.team_roles || []).includes(form.working_title)
                    ? form.working_title
                    : form.working_title
                    ? OTHER
                    : NONE
                }
                onValueChange={(v) =>
                  set("working_title", v === NONE ? "" : v === OTHER ? " " : v)
                }
                disabled={selfOnly}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-team-role">
                  <SelectValue placeholder="Not stated" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  <SelectItem value={NONE}>Not stated</SelectItem>
                  {(meta?.team_roles || []).map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER}>Something else…</SelectItem>
                </SelectContent>
              </Select>
              {!!form.working_title && !(meta?.team_roles || []).includes(form.working_title) && (
                <Input
                  className="mt-2 h-11 rounded-none"
                  data-testid="person-working-title"
                  placeholder="What they are called in the team"
                  value={form.working_title.trim()}
                  onChange={(e) => set("working_title", e.target.value || " ")}
                  disabled={selfOnly}
                />
              )}
            </Field>
            <div className="sm:col-span-2">
              <label className="label-caps">Group lead — shown beside the SPOC of every unit</label>
              <p className="mb-2 mt-1 text-[10px] text-muted-foreground">
                Leave every unit unpicked for an ordinary team member.
              </p>
              <UnitsPicker
                plants={plants || []}
                value={form.lead_units || []}
                onChange={(v) => set("lead_units", v)}
                testId="person-lead-units"
              />
            </div>
          </Block>

          <Block
            title="What they can do on the portal"
            note={
              selfOnly
                ? "What you can do on the portal is SDTD's to set."
                : mayAccess
                ? isSystemAdmin
                  ? "Applies at once. The officer is told by mail what changed and who changed it."
                  : "A unit SPOC may switch their own team between a designated member and read-only."
                : "Set by an SDTD Administrator."
            }
            testId="person-access"
          >
            <Field label="What they can do">
              <Select
                value={form.role}
                onValueChange={(v) => set("role", v)}
                disabled={!mayAccess}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-role">
                  <SelectValue placeholder="No portal account" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  <SelectItem value={NONE}>No portal account</SelectItem>
                  {(meta?.roles || [])
                    .filter((r) =>
                      isSystemAdmin ? true : ["spoc_member", "viewer"].includes(r),
                    )
                    .map((r) => (
                      <SelectItem key={r} value={r}>
                        {meta?.role_labels?.[r] || r}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Access" hint="An SDTD Administrator switches access on or off.">
              <Select
                value={form.access_status}
                onValueChange={(v) => set("access_status", v)}
                disabled={!isSystemAdmin || !form.user_id}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="person-access-status">
                  <SelectValue placeholder="Not on the portal" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  <SelectItem value={NONE}>Leave as it is</SelectItem>
                  <SelectItem value="active">On — they can sign in</SelectItem>
                  <SelectItem value="deactivated">Off — they cannot sign in</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {clean(form.role) && clean(form.role) !== NONE && (
              <div className="sm:col-span-2">
                <label className="label-caps">
                  Other units this officer also covers
                </label>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  The unit above is their home unit — it scopes their dashboard and their use
                  cases. Anything added here is a unit they may also work on.
                </p>
                <div className="mt-2">
                  <UnitsPicker
                    plants={(plants || []).filter((p) => p.code !== form.plant)}
                    value={form.units || []}
                    onChange={(v) => set("units", v)}
                    testId="person-units"
                  />
                </div>
              </div>
            )}
            {!form.user_id && !!form.email && (
              <label className="sm:col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="person-invite"
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                  checked={!!form.invite}
                  onChange={(e) => set("invite", e.target.checked)}
                />
                Invite them to the portal in this same Save — the passcode travels with the
                invitation
              </label>
            )}
            {amSuperAdmin && !selfOnly && (
              <div className="sm:col-span-2 border border-border p-3" data-testid="person-super-admin-block">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="person-super-admin"
                    className="h-4 w-4 accent-[hsl(var(--primary))]"
                    checked={!!form.super_admin}
                    disabled={!mayManageSuper || isFounderEmail}
                    onChange={(e) => set("super_admin", e.target.checked)}
                  />
                  Super-admin of the portal
                </label>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {isFounderEmail
                    ? "One of the portal's two founding super-admins — this cannot be taken off, by anybody."
                    : mayManageSuper
                    ? "A super-admin signs in on the standing passcode and may switch Super-Admin mode on. Taking it off leaves their account and their role untouched."
                    : "Only a super-admin may add another or take one off."}
                </p>
              </div>
            )}
          </Block>

          <Field label="A line for the trail (optional)" wide>
            <Input
              className="mt-1 h-11 rounded-none"
              data-testid="person-reason"
              placeholder="e.g. transferred to RSP on promotion"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>

          {warn && (
            <div
              className="flex gap-3 border border-[hsl(var(--watch))] bg-muted p-3 text-xs"
              data-testid="person-orphan-warning"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--watch))]" />
              <span className="leading-relaxed">{warn}</span>
            </div>
          )}

          {removing && (
            <div
              className="border border-[hsl(var(--at-risk))] p-3 text-xs"
              data-testid="person-remove-block"
            >
              {form.name} will be taken off the {form.plant} team and their portal access switched
              off. Please give the reason above, then confirm.
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            {editing && (isSystemAdmin || isSpoc) ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-none border-[hsl(var(--at-risk))] text-[hsl(var(--at-risk))]"
                data-testid={removing ? "person-remove-confirm" : "person-remove"}
                disabled={busy}
                onClick={() => (removing ? save(true, true) : setRemoving(true))}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {removing ? "Yes, remove them" : "Remove"}
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid={warn ? "person-save-anyway" : "save-person-button"}
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
              onClick={(e) => {
                if (warn) {
                  e.preventDefault();
                  save(true, false);
                }
              }}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : warn ? (
                "Save anyway"
              ) : editing ? (
                "Save everything"
              ) : (
                "Add this person"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
